import type { RefObject } from "react";
import {
  ChevronDown, CircleHelp, Copy, FileImage, FileText, Loader2, Paperclip, Trash2, Upload,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { DateTimePicker } from "@/shared/components/DateTimePicker/DateTimePicker";
import {
  AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogCancel,
} from "@/shared/ui/AlertDialog";
import { Button } from "@/shared/ui/Button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/shared/ui/DropDownMenu";
import { Input } from "@/shared/ui/Input";
import { Popover, PopoverAnchor, PopoverContent } from "@/shared/ui/Popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/Tooltip";
import {
  CREDIT_LOCKED_HINT,
  DEBIT_LOCKED_HINT,
  LOCKED_AMOUNT_INPUT_CLASS,
  formatCurrencyField,
  getAmountSideLock,
  isImageAttachment,
  normalizeSearchText,
  normalizeCurrencyTyping,
} from "./utils";
import type { EditableMovementState, MovementAttachmentState } from "./utils";
import type { BankMovement, BankMovementAccount } from "../../types/accounting.types";

type BankMovementEditorProps = {
  bankMovement: BankMovement | null
  bankMovementId: string
  movementDraft: EditableMovementState | null
  setMovementField: (field: keyof EditableMovementState, value: string) => void
  isMovementBusy: boolean
  isPersistedMovement: boolean
  canSaveMovement: boolean
  isBusy: boolean
  isCreatePending: boolean
  serviceBridge: boolean
  // Account popover
  accountPopoverOpen: boolean
  setAccountPopoverOpen: (open: boolean) => void
  accountSearch: string
  setAccountSearch: (value: string) => void
  bankAccounts: BankMovementAccount[]
  isBankAccountsLoading: boolean
  selectedAccount: BankMovementAccount | null
  // Payment mode popover
  paymentModePopoverOpen: boolean
  setPaymentModePopoverOpen: (open: boolean) => void
  paymentModeSearch: string
  setPaymentModeSearch: (value: string) => void
  filteredPaymentModes: string[]
  isPaymentModesLoading: boolean
  // Attachment
  movementAttachment: MovementAttachmentState | null
  attachmentInputRef: RefObject<HTMLInputElement | null>
  onUploadAttachment: (file: File) => void
  onDeleteAttachment: () => void
  isUploadingAttachment: boolean
  isDeletingAttachment: boolean
  // Actions
  onSaveMovement: () => void
  onAddAllocation: (type: "simple" | "activity" | "stock") => void
  onOpenRefundSearch: () => void
  onOpenEcommerceSearch: () => void
  onDeleteMovement: () => void
  isDeleteMovementBusy: boolean
  isMovementLocked?: boolean
  // Duplicate
  onDuplicate?: (date: string) => Promise<void>
  isDuplicateBusy?: boolean
  hasSettlement?: boolean
};

const BankMovementEditor = ({
  bankMovement,
  bankMovementId: _bankMovementId,
  movementDraft,
  setMovementField,
  isMovementBusy,
  isPersistedMovement,
  canSaveMovement,
  isBusy,
  isCreatePending,
  serviceBridge,
  accountPopoverOpen,
  setAccountPopoverOpen,
  accountSearch,
  setAccountSearch,
  bankAccounts,
  isBankAccountsLoading,
  selectedAccount,
  paymentModePopoverOpen,
  setPaymentModePopoverOpen,
  paymentModeSearch,
  setPaymentModeSearch,
  filteredPaymentModes,
  isPaymentModesLoading,
  movementAttachment,
  attachmentInputRef,
  onUploadAttachment,
  onDeleteAttachment,
  isUploadingAttachment,
  isDeletingAttachment,
  onSaveMovement,
  onAddAllocation,
  onOpenRefundSearch,
  onOpenEcommerceSearch,
  onDeleteMovement,
  isDeleteMovementBusy,
  isMovementLocked,
  onDuplicate,
  isDuplicateBusy,
  hasSettlement,
}: BankMovementEditorProps) => {
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateDate, setDuplicateDate] = useState<string | null>(null);
  const isSepaRemittance = ["Remise de virements", "Remise de virements scindée"].includes(
    movementDraft?.paymentMode ?? bankMovement?.paymentMode ?? "",
  );
  const depositDateLabel = isSepaRemittance ? "Date d’exécution demandée" : "Date de dépôt bancaire";
  const isAttachmentBusy = isUploadingAttachment || isDeletingAttachment;
  const attachmentTypeLabel = movementAttachment?.fileType?.toUpperCase() === "PDF" ? "PDF" : "Image";
  const attachmentDisplayName = movementAttachment?.fileName || "Pièce jointe";
  const attachmentIsImage = isImageAttachment(movementAttachment?.fileType, movementAttachment?.url);

  const { isDebitLocked, isCreditLocked } = getAmountSideLock(movementDraft?.debit, movementDraft?.credit);

  const filteredBankAccounts = useMemo(() => {
    const query = normalizeSearchText(accountSearch);
    if (!query) return bankAccounts;
    return bankAccounts.filter((account) => normalizeSearchText(account.name).includes(query));
  }, [accountSearch, bankAccounts]);

  return (
    <div className="rounded-md border bg-white p-3 mb-4 space-y-3 [&_label]:!text-[12px] [&_label]:font-normal [&_label]:leading-tight [&_input]:!text-[12px] [&_input]:md:!text-[12px] [&_input]:leading-tight">
      <div className="space-y-2.5">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
          <div className="space-y-1 md:col-span-3">
            <label className="text-[12px] leading-tight text-slate-600">Date</label>
            <DateTimePicker
              value={movementDraft?.date || null}
              onValueChange={(value) => setMovementField("date", value ?? "")}
              showTime={false}
              allowPastDates
              disabled={isMovementLocked || isMovementBusy || !movementDraft}
            />
          </div>
          <div className="space-y-1 md:col-span-5">
            <label className="text-[12px] leading-tight text-slate-600">Libellé</label>
            <Input
              value={movementDraft?.label ?? ""}
              onChange={(event) => setMovementField("label", event.target.value)}
              className="h-8 !text-[12px] md:!text-[12px] leading-tight"
              disabled={isMovementLocked || isMovementBusy || !movementDraft}
            />
          </div>
          <div className="space-y-1 md:col-span-4">
            <label className="text-[12px] leading-tight text-slate-600">Compte</label>
            <Popover open={isMovementLocked ? false : accountPopoverOpen} onOpenChange={setAccountPopoverOpen}>
              <PopoverAnchor asChild>
                <div className="relative">
                  <Input
                    value={accountPopoverOpen ? accountSearch : (selectedAccount?.name || bankMovement?.accountName || "")}
                    onFocus={() => {
                      if (isMovementLocked) return;
                      setAccountSearch("");
                      setAccountPopoverOpen(true);
                    }}
                    onClick={() => { if (!isMovementLocked) setAccountPopoverOpen(true); }}
                    onChange={(event) => {
                      setAccountSearch(event.target.value);
                      setAccountPopoverOpen(true);
                    }}
                    placeholder={isBankAccountsLoading ? "Chargement..." : "Rechercher un compte"}
                    className="h-8 !text-[12px] md:!text-[12px] leading-tight pr-8"
                    disabled={isMovementLocked || isMovementBusy || !movementDraft || isBankAccountsLoading}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">▼</span>
                </div>
              </PopoverAnchor>
              <PopoverContent align="start" className="w-[360px] p-1">
                <div className="max-h-52 overflow-y-auto">
                  {filteredBankAccounts.length === 0 && (
                    <div className="px-2 py-1.5 text-[12px] text-slate-500">Aucun compte trouvé</div>
                  )}
                  {filteredBankAccounts.map((account) => (
                    <button
                      key={account.id}
                      type="button"
                      className="w-full text-left px-2 py-1.5 text-[12px] rounded hover:bg-slate-100"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setMovementField("accountId", account.id);
                        setAccountSearch("");
                        setAccountPopoverOpen(false);
                      }}
                    >
                      {account.name}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
          <div className="space-y-1 md:col-span-4">
            <label className="text-[12px] leading-tight text-slate-600">Mode de règlement</label>
            <Popover open={isMovementLocked ? false : paymentModePopoverOpen} onOpenChange={setPaymentModePopoverOpen}>
              <PopoverAnchor asChild>
                <div className="relative">
                  <Input
                    value={paymentModePopoverOpen ? paymentModeSearch : (movementDraft?.paymentMode || "")}
                    onFocus={() => {
                      if (isMovementLocked) return;
                      setPaymentModeSearch("");
                      setPaymentModePopoverOpen(true);
                    }}
                    onClick={() => { if (!isMovementLocked) setPaymentModePopoverOpen(true); }}
                    onChange={(event) => {
                      setPaymentModeSearch(event.target.value);
                      setPaymentModePopoverOpen(true);
                    }}
                    placeholder={isPaymentModesLoading ? "Chargement..." : "Rechercher un mode"}
                    className="h-8 !text-[12px] md:!text-[12px] leading-tight pr-8"
                    disabled={isMovementLocked || isMovementBusy || !movementDraft || isPaymentModesLoading}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">▼</span>
                </div>
              </PopoverAnchor>
              <PopoverContent align="start" className="w-[360px] p-1">
                <div
                  className="max-h-52 overflow-y-auto overscroll-contain [touch-action:pan-y]"
                  onWheelCapture={(event) => event.stopPropagation()}
                >
                  {filteredPaymentModes.length === 0 && (
                    <div className="px-2 py-1.5 text-[12px] text-slate-500">Aucun mode trouvé</div>
                  )}
                  {filteredPaymentModes.map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      className="w-full text-left px-2 py-1.5 text-[12px] rounded hover:bg-slate-100"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setMovementField("paymentMode", mode);
                        setPaymentModeSearch("");
                        setPaymentModePopoverOpen(false);
                      }}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-1 md:col-span-4">
            <label className="text-[12px] leading-tight text-slate-600">Montant débit</label>
            <Input
              aria-label="Montant débit"
              inputMode="decimal"
              value={movementDraft?.debit ?? ""}
              onChange={(event) => setMovementField("debit", normalizeCurrencyTyping(event.target.value))}
              onBlur={(event) => setMovementField("debit", formatCurrencyField(event.target.value))}
              className={`h-8 !text-[12px] md:!text-[12px] leading-tight tabular-nums text-right ${isDebitLocked ? LOCKED_AMOUNT_INPUT_CLASS : ""}`}
              placeholder="0,00"
              disabled={isMovementLocked || isMovementBusy || !movementDraft}
              readOnly={isDebitLocked}
              aria-disabled={isDebitLocked}
              title={isDebitLocked ? DEBIT_LOCKED_HINT : undefined}
            />
          </div>
          <div className="space-y-1 md:col-span-4">
            <label className="text-[12px] leading-tight text-slate-600">Montant crédit</label>
            <Input
              aria-label="Montant crédit"
              inputMode="decimal"
              value={movementDraft?.credit ?? ""}
              onChange={(event) => setMovementField("credit", normalizeCurrencyTyping(event.target.value))}
              onBlur={(event) => setMovementField("credit", formatCurrencyField(event.target.value))}
              className={`h-8 !text-[12px] md:!text-[12px] leading-tight tabular-nums text-right ${isCreditLocked ? LOCKED_AMOUNT_INPUT_CLASS : ""}`}
              placeholder="0,00"
              disabled={isMovementLocked || isMovementBusy || !movementDraft}
              readOnly={isCreditLocked}
              aria-disabled={isCreditLocked}
              title={isCreditLocked ? CREDIT_LOCKED_HINT : undefined}
            />
          </div>
        </div>
      </div>

      {!isMovementLocked && (
        <div className="rounded-md border border-slate-200 bg-slate-50/60 p-2">
          <div className="text-[12px] leading-tight font-medium text-slate-600 mb-1.5">Champs optionnels</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <div className="space-y-1">
              <label className="text-[12px] leading-tight text-slate-600 flex min-h-4 items-center gap-1">
                N° paiement (Ref)
                {serviceBridge && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex h-3.5 w-3.5 items-center justify-center text-slate-400 hover:text-slate-600"
                        aria-label="Information sur le champ N° paiement (Ref)"
                      >
                        <CircleHelp className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-[11px] leading-snug">
                      Ce champ vous servira à réconcilier vos écritures saisies via l&apos;admin avec les écritures qui remontent par Bridge.
                    </TooltipContent>
                  </Tooltip>
                )}
              </label>
              <Input
                value={movementDraft?.paymentNumber ?? ""}
                onChange={(event) => setMovementField("paymentNumber", event.target.value)}
                className="h-8 !text-[12px] md:!text-[12px] leading-tight"
                disabled={isMovementBusy || !movementDraft}
              />
            </div>
            <div className="space-y-1">
              <label className="flex min-h-4 items-center text-[12px] leading-tight text-slate-600">N° de remise bancaire</label>
              <Input
                value={movementDraft?.remittanceNumber ?? ""}
                onChange={(event) => setMovementField("remittanceNumber", event.target.value)}
                className="h-8 !text-[12px] md:!text-[12px] leading-tight"
                disabled={isMovementBusy || !movementDraft}
              />
            </div>
            <div className="space-y-1">
              <label className="flex min-h-4 items-center text-[12px] leading-tight text-slate-600">{depositDateLabel}</label>
              <DateTimePicker
                className="h-8 [&_input]:h-full [&_input]:text-[12px]"
                ariaLabel={depositDateLabel}
                value={movementDraft?.depositDate || null}
                onValueChange={(value) => setMovementField("depositDate", value ?? "")}
                showTime={false}
                allowPastDates
                disabled={isMovementBusy || !movementDraft}
              />
            </div>
            <div className="space-y-1">
              <label className="flex min-h-4 items-center text-[12px] leading-tight text-slate-600">Date d&apos;émission</label>
              <DateTimePicker
                className="h-8 [&_input]:h-full [&_input]:text-[12px]"
                value={movementDraft?.issueDate || null}
                onValueChange={(value) => setMovementField("issueDate", value ?? "")}
                showTime={false}
                allowPastDates
                disabled={isMovementBusy || !movementDraft}
              />
            </div>
          </div>
          <div className="mt-2 space-y-1">
            <label className="text-[12px] leading-tight text-slate-600">Pièce jointe</label>
            <div className="rounded-md border border-slate-200 bg-white p-2">
              <input
                type="file"
                accept="image/png,image/jpeg,application/pdf"
                className="hidden"
                ref={attachmentInputRef}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) {
                    onUploadAttachment(file);
                  }
                  event.currentTarget.value = "";
                }}
                disabled={isMovementBusy || isAttachmentBusy || !movementDraft}
              />
              {!movementAttachment?.url
                ? (
                  <div className="flex items-center justify-between gap-2 rounded-md border border-dashed border-slate-300 bg-slate-50 px-2 py-1.5">
                    <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-600">
                      <Paperclip className="h-3.5 w-3.5 text-slate-500" />
                      Aucun fichier
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-7 px-2 text-xs bg-white"
                      onClick={() => attachmentInputRef.current?.click()}
                      disabled={isMovementBusy || isAttachmentBusy || !movementDraft}
                    >
                      {isUploadingAttachment
                        ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Ajout...
                          </span>
                        )
                        : (
                          <span className="inline-flex items-center gap-1.5">
                            <Upload className="h-3.5 w-3.5" />
                            Ajouter un fichier
                          </span>
                        )}
                    </Button>
                  </div>
                )
                : (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <a
                        href={movementAttachment.url}
                        target="_blank"
                        rel="noreferrer"
                        className="shrink-0"
                        title="Ouvrir la pièce jointe"
                      >
                        {attachmentIsImage
                          ? (
                            <img
                              src={movementAttachment.url}
                              alt={attachmentDisplayName}
                              className="h-12 w-12 rounded-md border border-slate-200 object-cover bg-white"
                              loading="lazy"
                            />
                          )
                          : (
                            <div className="h-12 w-12 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center">
                              {attachmentTypeLabel === "PDF"
                                ? <FileText className="h-4 w-4 text-slate-500" />
                                : <FileImage className="h-4 w-4 text-slate-500" />}
                            </div>
                          )}
                      </a>
                      <div className="min-w-0 flex-1">
                        <a
                          href={movementAttachment.url}
                          target="_blank"
                          rel="noreferrer"
                          className="truncate block text-[12px] leading-tight font-medium text-slate-800 hover:underline"
                          title={attachmentDisplayName}
                        >
                          {attachmentDisplayName}
                        </a>
                        <span className="mt-1 inline-flex shrink-0 rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                          {attachmentTypeLabel}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        className="h-7 px-2 text-xs bg-white"
                        onClick={() => attachmentInputRef.current?.click()}
                        disabled={isMovementBusy || isAttachmentBusy || !movementDraft}
                        title="Remplacer la pièce jointe"
                      >
                        {isUploadingAttachment
                          ? (
                            <span className="inline-flex items-center gap-1.5">
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              Remplacement...
                            </span>
                          )
                          : (
                            <span className="inline-flex items-center gap-1.5">
                              <Upload className="h-3.5 w-3.5" />
                              Remplacer
                            </span>
                          )}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-7 px-2 text-xs text-rose-700 hover:bg-rose-50"
                        onClick={onDeleteAttachment}
                        disabled={isMovementBusy || isAttachmentBusy}
                        title="Supprimer la pièce jointe"
                      >
                        {isDeletingAttachment
                          ? (
                            <span className="inline-flex items-center gap-1.5">
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              Suppression...
                            </span>
                          )
                          : (
                            <span className="inline-flex items-center gap-1.5">
                              <Trash2 className="h-3.5 w-3.5" />
                              Supprimer
                            </span>
                          )}
                      </Button>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {!isMovementLocked && (
        <div className="flex items-center justify-end gap-2">
          {isPersistedMovement && (
            <Button
              type="button"
              variant="outline"
              onClick={onDeleteMovement}
              disabled={isDeleteMovementBusy || isMovementBusy}
              className="h-8 px-3 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
            >
              Supprimer le mouvement
            </Button>
          )}
          {isPersistedMovement && onDuplicate && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (hasSettlement) {
                  toast.error("Impossible de dupliquer : un règlement est associé à une affectation.");
                  return;
                }
                setDuplicateDate(bankMovement?.date ?? null);
                setDuplicateDialogOpen(true);
              }}
              disabled={isMovementBusy}
              className="h-8 px-3 text-xs"
            >
              <span className="inline-flex items-center gap-1.5">
                <Copy className="h-3.5 w-3.5" />
                Dupliquer
              </span>
            </Button>
          )}
          <Button
            type="button"
            variant={canSaveMovement ? "success" : "outline"}
            onClick={onSaveMovement}
            disabled={!bankMovement || isMovementBusy || !canSaveMovement}
            className="h-8 px-3 text-xs"
          >
            {isMovementBusy
              ? (
                <span className="inline-flex items-center gap-1.5">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Enregistrement...
                </span>
              )
              : "Enregistrer le mouvement"}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="success"
                disabled={!bankMovement || !isPersistedMovement || isBusy || isCreatePending}
                className="h-8 px-3 text-xs"
                title={!isPersistedMovement ? "Enregistrez d'abord le mouvement" : undefined}
              >
                {isCreatePending
                  ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Ajout...
                    </span>
                  )
                  : (
                    <span className="inline-flex items-center gap-1.5">
                      Ajouter une affectation
                      <ChevronDown className="h-3.5 w-3.5" />
                    </span>
                  )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => onAddAllocation("simple")}>
                Écriture simple
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onOpenEcommerceSearch()}>
                Écriture &quot;Bons plans&quot;
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onOpenRefundSearch()}>
                Écriture remboursement
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onAddAllocation("stock")}>
                Écriture stock
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <AlertDialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dupliquer le mouvement</AlertDialogTitle>
            <AlertDialogDescription>
              Choisissez la date du nouveau mouvement. Cette date sera également appliquée aux affectations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <label className="block text-xs text-slate-600 mb-1">Date</label>
            <DateTimePicker
              value={duplicateDate}
              onValueChange={(value) => setDuplicateDate(value ?? null)}
              showTime={false}
              allowPastDates
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDuplicateBusy}>Annuler</AlertDialogCancel>
            <Button
              type="button"
              variant="action"
              disabled={!duplicateDate || isDuplicateBusy}
              onClick={async () => {
                if (!duplicateDate || !onDuplicate) return;
                await onDuplicate(duplicateDate);
                setDuplicateDialogOpen(false);
              }}
            >
              {isDuplicateBusy
                ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Duplication...
                  </span>
                )
                : "Dupliquer"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BankMovementEditor;
